import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  ChangeEvent,
} from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { valuesProps } from "types/paymentMethods";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import { VisaIcon } from "icons/payments/VisaIcon";

import JCB from "icons/payments/JCB.svg";
import amex from "icons/payments/amex.svg";
import dinersClub from "icons/payments/dinersClub.svg";
import UnionPay from "icons/payments/UnionPay";
import DiscoverIcon from "icons/payments/Discover";
import InputField from "components/form/InputField";
import { SettingsValue } from "types/payments";
import { useUserData } from "store/slices/authSlice";
import { buildStripeBillingDetails } from "utils/stripeBillingDetails";
import InputSelect from "components/form/InputSelect";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { countryAndState } from "store/slices/documentsSlice";
import { AddressDetails, countryAddressList } from "utils/countryAddressList";
import { useAppDispatch } from "store/hooks";
import {
  checkAddress,
  checkZip,
  detectCountry,
  validateCard,
} from "utils/HelperFunctions";
import { CardFormCheckType, StateOption, CountryOption } from "types/common";
import Button from "components/form/Button";
import {
  countryListGet,
  useCountryList,
  walletsProfile,
} from "store/slices/paymentMethodSlice";
import { SelectedState } from "types/paymentMethods";
import { getIntent } from "store/slices/paymentsSlice";
import { useTid } from "utils/testid";

// Stripe Card Element Event Types

type StripeCardEvent = {
  elementType: string;
};

// Card Brand Types
type CardBrand =
  | "visa"
  | "amex"
  | "mc"
  | "mastercard"
  | "discover"
  | "ds"
  | "jcb"
  | "diners"
  | "dinersclub"
  | "diners club"
  | "dc"
  | "unionpay"
  | string;

interface CheckoutFormProps {
  handleChange: (
    e:
      | ChangeEvent<HTMLInputElement>
      | { value?: string; name?: string }
      | { target?: { value?: any } },
    value: string
  ) => void;
  isEdit?: boolean;
  values: valuesProps;
  iconType: string;
  isDateErr: boolean;
  selectedState: SelectedState | undefined;
  setSelectedState: (state: SelectedState | undefined) => void;
  disableInputFields?: boolean;
  show?: boolean;
  setIconType: (val: string) => void;
  accountid?: string;
  clusterId?: string;
  email?: string;
  leadId?: string | number;
  setSuccessScreen: (val: boolean) => void;
}

const CardCheckoutForm: React.FC<CheckoutFormProps> = ({
  handleChange,
  isEdit,
  values,
  iconType,
  selectedState,
  setSelectedState,
  disableInputFields,
  show,
  setIconType,
  accountid,
  clusterId,
  leadId,
  email,
  setSuccessScreen,
}) => {
  const tid = useTid();
  const [cardError, setCardError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formCheck, setFormCheck] = useState<CardFormCheckType>({
    card: false,
    expiry: false,
    cvv: false,
  });
  const [formEmptyCheck, setFormEmptyCheck] = useState<CardFormCheckType>({
    card: true,
    expiry: true,
    cvv: true,
  });
  const [isErr, setIsErr] = useState<string | null>(null);
  const stripe = useStripe();
  const elements = useElements();
  const elementOptions: Record<string, any> = {
    style: {
      base: {
        fontSize: "12px",
        color: "#19224c",
        "::placeholder": {
          color: "#8c90a5",
        },
      },
      invalid: {
        color: "#ff0000",
      },
    },
  };

  const cardNumberElementOptions: Record<string, any> = {
    ...elementOptions,
    disableLink: true,
  };

  const countryList: CountryOption[] = useCountryList();
  const dispatch = useAppDispatch();
  const portalSettings = usePortalSettingsData();
  const [fieldArr, setFieldArr] = useState<string[] | undefined>();
  const [fieldObj, setFieldObj] = useState<AddressDetails | undefined>();

  const onCountryListGet = async (): Promise<void> => {
    try {
      await dispatch(countryListGet()).unwrap();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log("conntry list get err: -->", error?.message);
    }
  };

  const [states, setStates] = useState<StateOption[]>([]);
  const [countriesWithStates, setCountriesWithStates] = useState<any[]>([]);
  const userData = useUserData();
  const initCountryRef = useRef<boolean>(false);
  const autoDetectRef = useRef<boolean>(false);

  useEffect(() => {
    autoDetectRef.current = false;
  }, []);

  const setCountry = async (): Promise<void> => {
    const userCountry = await detectCountry();
    if (userCountry) handleChange({ value: userCountry }, "x_country");
  };

  useEffect(() => {
    if (countryList.length === 0) {
      onCountryListGet();
      return;
    }
    if (
      !isEdit &&
      !autoDetectRef.current &&
      (!values?.x_country || values?.x_country === "US")
    ) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [countryList]);

  useEffect(() => {
    if (values?.x_country) {
      let data = countryAddressList()[values?.x_country || "US"];
      if (data) {
        const arr = Object.keys(data).map((field) => field);
        const obj = countryAddressList()[values?.x_country || "US"];
        setFieldArr(arr);
        setFieldObj(obj);
      }
    }
    if (isEdit && values?.x_country) {
      const nStates = getStatesForCountryCode(values?.x_country);
      setStates(nStates);
      if (values?.x_state) {
        const selectedStateObject: any = nStates.find(
          (state: any) => state.name === values?.x_state
        );
        if (selectedStateObject) {
          setSelectedState(selectedStateObject);
          handleChange(selectedStateObject?.name, "x_state");
        }
      }
    } else if (isEdit && !values?.x_country) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [isEdit, values?.x_country]);

  useEffect(() => {
    if (show && !isEdit && countryList?.length > 0) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [show, isEdit, countryList]);

  const getPostalCode = (): string | false => {
    if (fieldArr && fieldArr.includes("CEDEX")) return "CEDEX";
    if (fieldArr && fieldArr.includes("ZIP")) return "ZIP";
    if (fieldArr && fieldArr.includes("Eircode")) return "Eircode";
    if (fieldArr && fieldArr.includes("PIN")) return "PIN";
    if (fieldArr && fieldArr.includes("Postal code")) return "Postal code";
    return false;
  };

  const getStateName = (): string | false => {
    if (fieldArr && fieldArr.includes("County")) return "County";
    if (fieldArr && fieldArr.includes("State")) return "State";
    if (fieldArr && fieldArr.includes("Department")) return "Department";
    if (fieldArr && fieldArr.includes("District")) return "District";
    if (fieldArr && fieldArr.includes("Emirate")) return "Emirate";
    if (fieldArr && fieldArr.includes("Island")) return "Island";
    if (fieldArr && fieldArr.includes("Oblast")) return "Oblast";
    if (fieldArr && fieldArr.includes("Parish")) return "Parish";
    if (fieldArr && fieldArr.includes("Prefecture")) return "Prefecture";
    if (fieldArr && fieldArr.includes("Province")) return "Province";
    return false;
  };
  const selectedCountryUI = useMemo(
    () => values?.x_country ?? "",
    [values?.x_country]
  );

  useEffect(() => {
    const getCountryAndState = async () => {
      try {
        const res: any = await dispatch(countryAndState()).unwrap();
        const data = res?.body?.data || [];
        setCountriesWithStates(data);
      } catch (error) {
        console.error(error);
      }
    };

    getCountryAndState();
  }, []);

  const getStatesForCountryCode = (code: string): StateOption[] => {
    try {
      const countryMeta = countryList?.find((c) => c?.value === code);
      const countryName = countryMeta?.name;
      if (!countryName) return [];
      const found = countriesWithStates?.find(
        (c: any) =>
          c?.country_name?.toLowerCase() === countryName?.toLowerCase()
      );
      const st = found?.states || [];
      return st.map((s: { state_name: string; state_code: string }) => ({
        name: s?.state_name,
        value: s?.state_name,
        state_code: s?.state_code,
      }));
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    if (
      values?.x_country &&
      countriesWithStates?.length > 0 &&
      countryList?.length > 0
    ) {
      const nStates = getStatesForCountryCode(values?.x_country);
      setStates(nStates);
    }
  }, [values?.x_country, countriesWithStates, countryList]);

  useEffect(() => {
    if (
      isEdit &&
      !initCountryRef.current &&
      selectedCountryUI &&
      countryList?.length > 0
    ) {
      const ev = countryList.find((c: any) => c?.value === selectedCountryUI);
      if (ev) {
        const nStates = getStatesForCountryCode(selectedCountryUI as string);
        setStates(nStates);
        handleChange(ev, "x_country");
        initCountryRef.current = true;
      }
    }
  }, [isEdit, selectedCountryUI, countryList, handleChange]);

  const imgUrl = (): React.ReactNode => {
    const brand: CardBrand = (
      values?.card_type ||
      iconType ||
      ""
    )?.toLowerCase();
    switch (brand) {
      case "visa":
        return <VisaIcon />;
      case "amex":
      case "american express":
      case "americanexpress":
      case "american expres":
        return <img src={amex} alt="Amex" />;
      case "mc":
      case "master":
      case "mastercard":
        return <MastercardIcon />;
      case "discover":
      case "ds":
        return <DiscoverIcon />;
      case "jcb":
        return <img src={JCB} alt="JCB" />;
      case "diners":
      case "dinersclub":
      case "diners club":
      case "dc":
        return <img src={dinersClub} alt="Diners Club" />;
      case "unionpay":
        return <UnionPay />;
      default:
        return null;
    }
  };

  const validateStripeCard = (e: StripeCardEvent): void => {
    if (e.elementType === "cardNumber" && !formCheck.card) {
      setCardError("Your card's number is incomplete.");
      return;
    }
    if (e.elementType === "cardExpiry" && !formCheck.expiry) {
      setCardError("Your card's expiration date is incomplete.");
      return;
    }
    if (e.elementType === "cardCvc" && !formCheck.cvv) {
      setCardError("Your card's security code is incomplete.");
      return;
    }

    if (formCheck.card && formCheck.expiry && formCheck.cvv) {
      setCardError(null);
      return;
    } else {
      setCardError(
        !formCheck.card && !formEmptyCheck.card
          ? "Your card's number is incomplete."
          : !formCheck.expiry && !formEmptyCheck.expiry
            ? "Your card's expiration date is incomplete."
            : !formCheck.cvv && !formEmptyCheck.cvv
              ? "Your card's security code is incomplete."
              : null
      );
      return;
    }
  };

  // The ZIP is sent even when the street address is hidden.
  const buildBillingDetails = () =>
    buildStripeBillingDetails({
      values,
      showAddress: portalSettings?.cc_billing_address === SettingsValue.YES,
      email,
      account: userData?.data?.accountData,
      states,
    });

  const handleSubmit = async (): Promise<void> => {
    if (!stripe || !elements || isDisabled) {
      return;
    }
    setIsLoading(true);
    setCardError(null);
    setIsErr(null);
    try {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) return;

      const billingDetails = buildBillingDetails();
      const payload = {
        total_amount: parseInt((parseFloat("0.00") * 100).toFixed(2)),
        payment_method_types: "card",
        accountid: accountid || "",
        cluster_id: clusterId,
        isRecurring: true,
        isWalletProfile: true,
        description: "Online Payment",
        payment_note: "One-time",
        // The same details as the card, for the Stripe customer.
        billing: billingDetails,
      };
      const res: any = await dispatch(getIntent(payload)).unwrap();

      if (res?.body?.success) {
        const clientSecret = res?.body?.data?.client_secret;
        const { error, setupIntent } = await stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: billingDetails,
            },
          }
        );
        if (error) {
          console.log("error ======", error);
          setIsErr(error.message || null);
        } else {
          const walletPayload: any = {
            lead_id: leadId,
            cluster_id: clusterId,
            accountid: accountid,
            module: "process_payment",
            setupIntent: setupIntent?.id,
            x_method: "CC",
            email: email,
            billing: billingDetails,
          };
          if (isEdit) {
            walletPayload["isEdit"] = true;
            walletPayload["cppid"] = values?.id;
          }
          const response: any = await dispatch(
            walletsProfile(walletPayload)
          ).unwrap();
          if (response?.body?.success) {
            setSuccessScreen(true);
          } else if (response?.meta?.requestStatus === "rejected") {
            setIsErr(response?.body?.data?.message || null);
          }
          console.log("setupIntent ======", setupIntent);
        }
      } else {
        setIsErr(res?.body?.data?.message || null);
      }
    } catch (error) {
      console.log("error ======", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = useMemo<boolean>(() => {
    // This form always collects the address it shows, so the mandatory
    // setting applies (without the last argument the check was skipped).
    const addressField = checkAddress(
      portalSettings?.cc_billing_address,
      portalSettings?.cc_billing_addr_mandatory,
      values,
      true
    );
    const validZip = checkZip(values);

    if (isEdit) {
      return (
        !addressField ||
        !values?.choldername ||
        !values?.choldername.trim() ||
        !validZip
      );
    } else {
      return (
        formCheck.card === false ||
        formCheck.expiry === false ||
        formCheck.cvv === false ||
        !addressField ||
        !values?.choldername ||
        !values?.choldername.trim() ||
        !validZip
      );
    }
  }, [formCheck, values, portalSettings]);

  return (
    <form
      className="h-full"
      data-testid={tid("checkout-card-form")}
      aria-label={
        isEdit ? "Update checkout card form" : "Add checkout card form"
      }
    >
      <div
        className="lg:px-10 px-5 w-full flex flex-col gap-5 items-start pt-5"
        data-testid={tid("checkout-card-fields")}
      >
        <div className="w-full" data-testid={tid("checkout-card-section")}>
          {isEdit ? (
            <>
              <div className="relative">
                <InputField
                  name="x_card_num"
                  label="card information"
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none border-b-0 `}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder="1234  1234  1234  1234"
                  value={"XXXX XXXX XXXX " + values?.x_card_num || ""}
                  onChange={(e) => console.log("e ======", e)}
                  disabled={true}
                  data-testid={tid("checkout-card-number-readonly-input")}
                  aria-label="Saved card number"
                />
                <div className="absolute right-[11px] top-[30px] w-[35px] h-[18px] flex">
                  {imgUrl()}
                </div>
              </div>
              <div className="grid grid-cols-2">
                <InputField
                  name="x_exp_date"
                  showLabel={false}
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !rounded-e-none `}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder="MM / YY"
                  value={values?.x_exp_date}
                  onChange={(e) => console.log("e ======", e)}
                  disabled={true}
                  data-testid={tid("checkout-card-expiry-input")}
                  aria-label="Card expiration date"
                />
                <InputField
                  type="number"
                  min={0}
                  name="x_card_code"
                  showLabel={false}
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none  !rounded-s-none !border-l-0`}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder="CVC"
                  value={"XXX"}
                  onChange={(e) => console.log("e ======", e)}
                  disabled={true}
                  data-testid={tid("checkout-card-cvc-input")}
                  aria-label="Card security code"
                />
              </div>
            </>
          ) : (
            <>
              <h2 className="text-txtSecondary text-xs font-bold uppercase mb-1">
                CARD INFORMATION
              </h2>
              <div
                className="w-full border border-strokePrimary rounded-[5px] text-txtPrimary"
                data-testid={tid("checkout-card-element-group")}
                aria-label="Card information"
              >
                <div
                  className="w-full border-b hover:!border-strokeActive border-strokePrimary py-2 px-4 text-xs relative"
                  data-testid={tid("checkout-card-number-element")}
                  aria-label="Card number"
                >
                  <CardNumberElement
                    options={cardNumberElementOptions}
                    onBlur={validateStripeCard}
                    onChange={(event) => {
                      if (event.brand !== "unknown") {
                        setIconType(event.brand);
                      }
                      if (event.empty && !formCheck.expiry && !formCheck.cvv) {
                        setCardError(null);
                      }
                      setFormEmptyCheck((prev) => ({
                        ...prev,
                        card: event.empty,
                      }));
                      setFormCheck((prev) => ({
                        ...prev,
                        card: event.complete,
                      }));
                    }}
                  />
                  {iconType?.length > 0 && (
                    <div
                      className={`absolute  right-4 top-1/2 transform -translate-y-1/2 text-xs capitalize ${
                        iconType ? "w-35px" : "w-174px"
                      }`}
                    >
                      {imgUrl()}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 w-full">
                  <div
                    className="border-r border-strokePrimary py-2 px-4 text-xs"
                    data-testid={tid("checkout-card-expiry-element")}
                    aria-label="Card expiration date"
                  >
                    <CardExpiryElement
                      options={elementOptions}
                      onBlur={validateStripeCard}
                      onChange={(event) => {
                        if (event.empty && !formCheck.card && !formCheck.cvv) {
                          setCardError(null);
                        }
                        setFormEmptyCheck((prev) => ({
                          ...prev,
                          expiry: event.empty,
                        }));
                        setFormCheck((prev) => ({
                          ...prev,
                          expiry: event.complete,
                        }));
                      }}
                    />
                  </div>
                  <div
                    className="py-2 px-4 text-xs"
                    data-testid={tid("checkout-card-cvc-element")}
                    aria-label="Card security code"
                  >
                    <CardCvcElement
                      options={elementOptions}
                      onBlur={validateStripeCard}
                      onChange={(event) => {
                        if (
                          event.empty &&
                          !formCheck.expiry &&
                          !formCheck.card
                        ) {
                          setCardError(null);
                        }
                        setFormEmptyCheck((prev) => ({
                          ...prev,
                          cvv: event.empty,
                        }));
                        setFormCheck((prev) => ({
                          ...prev,
                          cvv: event.complete,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {cardError && (
            <div className="text-xs text-red pt-1">{cardError}</div>
          )}
        </div>

        <div
          className="w-full"
          data-testid={tid("checkout-cardholder-section")}
        >
          <InputField
            name="choldername"
            label="Cardholder name"
            className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
            _className="!text-xs !font-bold !leading-normal"
            placeholder="Full name on card"
            value={values?.choldername}
            onChange={(e) => handleChange(e, "choldername")}
            onBlur={() => {
              validateStripeCard({ elementType: "choldername" });
            }}
            tabInd={4}
            disabled={disableInputFields}
            data-testid={tid("checkout-cardholder-name-input")}
            aria-label="Cardholder name"
          />
        </div>

        {portalSettings?.cc_billing_address === SettingsValue.YES && (
          <div
            className="w-full"
            data-testid={tid("checkout-billing-address-section")}
          >
            <InputSelect
              label="Billing Address"
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
              _className="!text-xs !font-bold !leading-normal"
              optionClassName="!text-xs"
              arrowClassName="!right-[11px] !top-[10px]"
              placeholder="Country"
              selected={selectedCountryUI}
              options={countryList}
              setSelected={(e: any) => {
                autoDetectRef.current = true;
                const nStates = getStatesForCountryCode(e?.value as string);
                setStates(nStates);
                setSelectedState(undefined);
                handleChange({ target: { value: "" } }, "x_state");
                handleChange(e, "x_country");
              }}
              tabInd={5}
              disabled={disableInputFields}
              data-testid={tid("checkout-billing-country-select")}
              aria-label="Billing country"
            />
            {fieldArr && fieldArr.includes("Address line 1") && (
              <InputField
                name="x_address"
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                  (fieldArr &&
                    (fieldArr.includes("City") ||
                      fieldArr.includes("Area") ||
                      fieldArr.includes("Address line 2"))) ||
                  getPostalCode() ||
                  getStateName()
                    ? "!rounded-none"
                    : "!rounded-b-[5px] !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder="Address line 1"
                value={values?.x_address}
                onChange={(e) => handleChange(e, "x_address")}
                tabInd={6}
                disabled={disableInputFields}
                data-testid={tid("checkout-billing-address-line-1-input")}
                aria-label="Billing address line 1"
              />
            )}
            {fieldArr && fieldArr.includes("Address line 2") && (
              <InputField
                name="address2"
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs  !border-t-0 ${
                  (fieldArr &&
                    (fieldArr.includes("City") || fieldArr.includes("Area"))) ||
                  getPostalCode() ||
                  getStateName()
                    ? "!rounded-none"
                    : "!rounded-b-[5px] !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder="Address line 2"
                value={values?.address2}
                onChange={(e) => handleChange(e, "address2")}
                tabInd={7}
                disabled={disableInputFields}
                data-testid={tid("checkout-billing-address-line-2-input")}
                aria-label="Billing address line 2"
              />
            )}
            <div
              className={`grid ${
                getPostalCode()
                  ? !fieldArr?.includes("City") && !fieldArr?.includes("Area")
                    ? "grid-cols-1"
                    : "grid-cols-2"
                  : "grid-cols-1"
              }`}
            >
              {fieldArr && fieldArr.includes("City") && (
                <InputField
                  name="x_city"
                  showLabel={false}
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                    getStateName()
                      ? "!rounded-none"
                      : getPostalCode()
                        ? "rounded-b-[5px] !rounded-r-none"
                        : "!rounded-[5px] !rounded-t-none"
                  }`}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder="City"
                  value={values?.x_city}
                  onChange={(e) => handleChange(e, "x_city")}
                  tabInd={8}
                  disabled={disableInputFields}
                  data-testid={tid("checkout-billing-city-input")}
                  aria-label="Billing city"
                />
              )}
              {fieldArr && fieldArr.includes("Area") && (
                <InputField
                  name="x_city"
                  showLabel={false}
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                    getStateName()
                      ? "!rounded-none"
                      : getPostalCode()
                        ? "rounded-b-[5px] !rounded-r-none"
                        : "!rounded-[5px] !rounded-t-none"
                  }`}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder="Area"
                  value={values?.x_city}
                  onChange={(e) => handleChange(e, "x_city")}
                  tabInd={8}
                  disabled={disableInputFields}
                  data-testid={tid("checkout-billing-area-input")}
                  aria-label="Billing area"
                />
              )}

              {getPostalCode() && (
                <InputField
                  name="x_zip"
                  type={fieldObj?.isNumber ? "number" : "text"}
                  showLabel={false}
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                    !fieldArr?.includes("City") && !fieldArr?.includes("Area")
                      ? ""
                      : "!border-l-0"
                  } ${
                    getStateName()
                      ? "!rounded-none"
                      : "!rounded-b-[5px] !rounded-l-none !rounded-t-none"
                  }`}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder={getPostalCode() || ""}
                  value={values?.x_zip}
                  onChange={(e) => {
                    if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                      handleChange(e, "x_zip");
                  }}
                  tabInd={9}
                  disabled={disableInputFields}
                  data-testid={tid("checkout-billing-postal-code-input")}
                  aria-label="Billing postal code"
                />
              )}
            </div>
            {states && states.length > 0 && (
              <InputSelect
                showLabel={false}
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !border-t-0 "
                _className="!text-xs !font-bold !leading-normal"
                optionClassName="!text-xs"
                placeholder={getStateName() || "State"}
                selected={selectedState}
                arrowClassName="!right-[11px] !top-[10px]"
                isManual
                options={states}
                setSelected={(e: any) => {
                  if (e?.value) {
                    setSelectedState(e);
                    handleChange(e, "x_state");
                  }
                }}
                tabInd={10}
                disabled={disableInputFields}
                data-testid={tid("checkout-billing-state-select")}
                aria-label="Billing state"
              />
            )}
          </div>
        )}

        {portalSettings?.cc_billing_address === SettingsValue.NO && (
          <div
            className="w-full"
            data-testid={tid("checkout-billing-postal-section")}
          >
            <InputSelect
              label="Billing Address"
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
              _className="!text-xs !font-bold !leading-normal"
              optionClassName="!text-xs"
              arrowClassName="!right-[11px] !top-[10px]"
              placeholder="Country"
              selected={selectedCountryUI}
              options={countryList}
              setSelected={(e: any) => {
                autoDetectRef.current = true;
                const nStates = getStatesForCountryCode(e?.value as string);
                setStates(nStates);
                setSelectedState(undefined);
                handleChange({ target: { value: "" } }, "x_state");
                handleChange(e, "x_country");
              }}
              tabInd={5}
              disabled={disableInputFields}
              data-testid={tid("checkout-billing-country-select")}
              aria-label="Billing country"
            />
            {getPostalCode() && (
              <InputField
                name="x_zip"
                type={fieldObj?.isNumber ? "number" : "text"}
                showLabel={false}
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-b-[5px] !border-t-0 !rounded-t-none "
                _className="!text-xs !font-bold !leading-normal"
                placeholder={getPostalCode() || ""}
                value={values?.x_zip}
                onChange={(e) => {
                  if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                    handleChange(e, "x_zip");
                }}
                tabInd={9}
                disabled={disableInputFields}
                data-testid={tid("checkout-billing-postal-code-input")}
                aria-label="Billing postal code"
              />
            )}
          </div>
        )}
      </div>
      {isErr && <div className="lg:px-10 px-5 text-xs text-red">{isErr}</div>}

      <div className="lg:px-10 px-5 pt-5">
        <Button
          text={isEdit ? "Update Card" : "Add Card"}
          className="w-full"
          isLoading={isLoading}
          isDisabled={isDisabled}
          onClick={handleSubmit}
          data-testid={tid("checkout-card-submit-button")}
          aria-label={isEdit ? "Update checkout card" : "Add checkout card"}
        />
      </div>
    </form>
  );
};

export default CardCheckoutForm;
